import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import _ from "lodash";
import { Breadcrumbs, Header, Workspace, LoadingState } from "components/Shared";
import { translate } from "lib";
import { getSigner, signAsSigner, declineAsSigner } from "actions/signers";
import { createDocumentUploadFirst } from "actions/documents";

// User D: preview generated PDF, upload signed PDF, complete
const SignerView = ({ match, history }: RouteComponentProps<{ id: string }>) => {
	const dispatch = useDispatch();
	const signerId = match.params.id;
	const signer = useSelector((state: any) => _.find(state.signers, { id: signerId }) as any);
	const condition = _.get(signer, "condition");
	const fileRef = useRef<HTMLInputElement>(null);
	const [note, setNote] = useState("");
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		dispatch(getSigner(signerId) as any);
	}, [dispatch, signerId]);

	if (!signer) return <LoadingState />;

	const generatedDoc = _.get(signer, "condition.generated_document") || _.get(signer, "generated_document");
	const generatedUri = _.get(generatedDoc, "revisions[0].signed_uri");

	const uploadSignedFile = async (file: File): Promise<string> => {
		// Upload first (see actions/documents#createDocumentUploadFirst): the
		// signed copy only becomes a Document once S3 has it, so a failed upload
		// leaves no record and onSign never signs against it.
		const conditionId = _.get(signer, "condition_id") || _.get(condition, "id");
		const doc = await createDocumentUploadFirst(`conditions/${conditionId}`, file, {
			mimetype: file.type || "application/pdf",
			documentable_type: "Condition",
			documentable_id: conditionId
		});
		return doc.id;
	};

	const onSign = async () => {
		const file = fileRef.current?.files?.[0] || null;
		setUploading(true);
		setError(null);
		try {
			let signatureDocumentId: string | undefined;
			if (file) signatureDocumentId = await uploadSignedFile(file);
			await dispatch(signAsSigner(signerId, signatureDocumentId, note) as any);
			history.push(`/tasks/${_.get(signer, "condition.task_id") || ""}`);
		} catch (e) {
			setError((e as any)?.message || translate("sign_failed"));
		} finally {
			setUploading(false);
		}
	};

	const onDecline = async () => {
		if (!window.confirm(translate("confirm_decline"))) return;
		await dispatch(declineAsSigner(signerId, note) as any);
		history.push(`/tasks/${_.get(signer, "condition.task_id") || ""}`);
	};

	return (
		<>
			<Breadcrumbs items={[{ title: "tasks" }, { title: "sign" }]} />
			<Header title={translate("sign_document")} />
			<Workspace id="signer-view" className="row">
				<div className="col-md-7">
					<div className="card">
						<div className="card-header">
							<h5 className="mb-0">{translate("preview")}</h5>
						</div>
						<div className="card-body" style={{ minHeight: 400 }}>
							{generatedUri ? (
								<iframe src={generatedUri} title="preview" style={{ width: "100%", height: 500, border: 0 }} />
							) : (
								<p className="text-muted">{translate("no_pdf_to_preview_yet")}</p>
							)}
							{generatedUri && (
								<a className="btn btn-sm btn-outline-primary mt-2" href={generatedUri} target="_blank" rel="noopener noreferrer">
									{translate("download_pdf")}
								</a>
							)}
						</div>
					</div>
				</div>
				<div className="col-md-5">
					<div className="card">
						<div className="card-header">
							<h5 className="mb-0">{translate("upload_signed_copy")}</h5>
						</div>
						<div className="card-body">
							{signer.status === "signed" ? (
								<div className="alert alert-success">
									{translate("already_signed_at")} {signer.signed_at}
								</div>
							) : (
								<>
									<div className="form-group">
										<label>{translate("signed_document_pdf")}</label>
										<input
											ref={fileRef}
											type="file"
											className="form-control-file"
											accept="application/pdf"
										/>
									</div>
									<div className="form-group">
										<label>{translate("note_optional")}</label>
										<textarea
											className="form-control"
											rows={2}
											value={note}
											onChange={(e) => setNote(e.target.value)}
										/>
									</div>
									{error && <div className="alert alert-danger py-2">{error}</div>}
									<div className="d-flex justify-content-between">
										<button className="btn btn-outline-danger" onClick={onDecline} disabled={uploading}>
											{translate("decline")}
										</button>
										<button className="btn btn-primary" onClick={onSign} disabled={uploading}>
											{uploading ? translate("submitting") : translate("complete_signature")}
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</Workspace>
		</>
	);
};

export default withRouter(SignerView);
